import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Descriptions, Divider, message, Modal, Spin } from 'antd';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import BookingOrder from '../types/BookingOrder';
import { useTranslation } from 'react-i18next';
import { DisplayedCourseInstance } from '../../courseInstance/types/CourseInstance';
import { getFullContactName } from '../utils/contactPerson';
import { formatDate, formatTime } from '../../utils/date';
import BookingOrderAPI from '../api/BookingOrderAPI';
import SubsidiaryCompanyAPI from '../../subsidiaryCompany/SubsidiaryCompanyAPI';
import { useQuery } from '@tanstack/react-query';
import { ReactComponent as PaperPlane } from '../../resources/img/paperPlane.svg';

type SendConfirmationEmailModalProps = {
  bookingOrder?: BookingOrder;
  setBookingOrder: (bookingOrder: BookingOrder) => void;
  displayedCourseInstances?: DisplayedCourseInstance[];
  showModal: boolean;
  onCancel: () => void;
};

dayjs.extend(localizedFormat);

const SendConfirmationEmailModal: React.FC<SendConfirmationEmailModalProps> = ({
  bookingOrder,
  setBookingOrder,
  displayedCourseInstances,
  showModal,
  onCancel,
}) => {
  const { t } = useTranslation();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const emailSenderAddressQuery = useQuery({
    queryKey: ['emailSenderAddressQueryKey', bookingOrder],
    enabled: !!bookingOrder?.id,
    queryFn: () => {
      if (!bookingOrder?.id) {
        return;
      }

      return SubsidiaryCompanyAPI.getEmailSenderAddress(
        bookingOrder.subsidiaryCompany.id,
      );
    },
  });

  useEffect(() => {
    if (emailSenderAddressQuery.isError) {
      message.error(
        t('components.SendConfirmationEmailModal.failedToFetchSenderEmail'),
      );
    }
  }, [emailSenderAddressQuery.isError, t]);

  const onSubmit = useCallback(() => {
    if (!bookingOrder?.contactPerson) {
      return;
    }

    setIsSubmitting(true);
    BookingOrderAPI.sendConfirmationEmail(bookingOrder.id)
      .then((response) => {
        message.success(
          t('components.SendConfirmationEmailModal.confirmationEmailSent', {
            email: bookingOrder.contactPerson?.email,
          }),
        );
        setBookingOrder(response.data);
        onCancel();
      })
      .catch(() =>
        message.error(
          t('components.SendConfirmationEmailModal.confirmationEmailFailed'),
        ),
      )
      .finally(() => setIsSubmitting(false));
  }, [
    bookingOrder?.contactPerson,
    bookingOrder?.id,
    onCancel,
    setBookingOrder,
    t,
  ]);

  const EmailInformationHeader: React.FC<{ bookingOrder?: BookingOrder }> = ({
    bookingOrder,
  }) => {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex">
          <div className="w-48 flex-shrink-0">
            {t('components.SendConfirmationEmailModal.emailSender')}:
          </div>
          {emailSenderAddressQuery.isFetching ? (
            <Spin />
          ) : (
            <div>
              <b>{bookingOrder?.subsidiaryCompany.name}</b> |{' '}
              {emailSenderAddressQuery.data?.data}
            </div>
          )}
        </div>
        {bookingOrder?.contactPerson && (
          <div className="flex">
            <div className="w-48 flex-shrink-0 ">
              {t('components.SendConfirmationEmailModal.emailRecipient')}:
            </div>
            <div>
              <div className="font-bold">
                {t('components.SendConfirmationEmailModal.purchaser')}
              </div>
              <div>{getFullContactName(bookingOrder.contactPerson)}</div>
              <div>{bookingOrder.contactPerson.email}</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const CourseInstanceRow: React.FC<{
    classNumber: number;
    courseInstance: DisplayedCourseInstance;
  }> = ({ classNumber, courseInstance }) => {
    const contactPersonInformation = useMemo(() => {
      if (!courseInstance.contactPerson) {
        return undefined;
      }
      return [
        getFullContactName(courseInstance.contactPerson),
        courseInstance.contactPerson.email,
        courseInstance.contactPerson.phoneNumber,
      ]
        .filter((it) => !!it)
        .join(' | ');
    }, [courseInstance]);

    return (
      <div>
        <div className="flex flex-row">
          <div className="w-48 flex-shrink-0 font-bold">
            {t('components.SendConfirmationEmailModal.classWithNumber', {
              classNumber: classNumber,
            })}
          </div>
          <div>
            <Descriptions column={1} className="order-confirmation-description">
              <Descriptions.Item
                label={t('components.SendConfirmationEmailModal.course')}>
                <div className="mb-4 font-bold">
                  {courseInstance.course.name}
                </div>
              </Descriptions.Item>
              <Descriptions.Item
                label={t('components.SendConfirmationEmailModal.date')}>
                {courseInstance.startsAt
                  ? formatDate(courseInstance.startsAt)
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item
                label={t('components.SendConfirmationEmailModal.time')}>
                {`${formatTime(courseInstance.startsAt)} -
                  ${formatTime(courseInstance.endsAt)}`}
              </Descriptions.Item>
              <Descriptions.Item
                label={t('components.SendConfirmationEmailModal.location')}>
                {courseInstance.address ? courseInstance.address : '-'}
              </Descriptions.Item>
              <Descriptions.Item
                label={t('components.SendConfirmationEmailModal.yourContact')}>
                {contactPersonInformation ?? '-'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        </div>
        <Divider />
      </div>
    );
  };

  return (
    <Modal
      className="w-full max-w-[1000px]"
      title={t('components.SendConfirmationEmailModal.orderOverview')}
      open={showModal}
      onCancel={onCancel}
      styles={{
        footer: {
          display: 'flex',
          justifyContent: 'flex-end',
        },
      }}
      okButtonProps={{
        icon: <PaperPlane className="flex" />,
        loading: isSubmitting,
        disabled:
          !bookingOrder ||
          !displayedCourseInstances ||
          displayedCourseInstances?.length === 0,
      }}
      onOk={onSubmit}
      okText={t('components.SendConfirmationEmailModal.sendOverviewEmail')}>
      <div className="pt-0">
        {t('components.SendConfirmationEmailModal.orderOverviewDescription')}
      </div>
      <Divider className="mt-4" />
      <EmailInformationHeader bookingOrder={bookingOrder} />
      <div className="font-bold mt-8">
        {t('components.SendConfirmationEmailModal.classInformation')}:
      </div>
      <Divider className="mt-2" dashed />
      {displayedCourseInstances?.map((courseInstance, index) => (
        <CourseInstanceRow
          key={courseInstance.id}
          classNumber={index + 1}
          courseInstance={courseInstance}
        />
      ))}
    </Modal>
  );
};

export default SendConfirmationEmailModal;
